/**
 * EngineRenderer — wraps THREE.WebGPURenderer with camera stack, resize handling,
 * and multi-camera rendering.
 */

import * as THREE from 'three/webgpu';
import { MAX_PIXEL_RATIO, DEFAULT_EXPOSURE } from '../shared/constants.js';
import { CameraStack, type CameraEntry } from './CameraStack.js';
import { Logger } from '../core/Logger.js';
import { PostProcessingPipeline, type PostProcessingConfig } from './PostProcessingPipeline.js';
import {
  detectRuntimeCaps,
  setRuntimeGpuCaps,
  desiredRequiredFeatures,
  type RuntimeGpuCaps,
} from './RuntimeCaps.js';
import { createGpuTimer, type GpuTimerRendererLike } from './timing/GpuTimerFactory.js';
import { GpuProfilerAggregator } from './timing/GpuProfilerAggregator.js';
import type { GpuTimingSource } from './timing/GpuTimingSource.js';
import type { IGpuTimerBackend } from './timing/GpuTimerBackend.js';

export interface RendererConfig {
  canvas: HTMLCanvasElement;
  antialias?: boolean;
  alpha?: boolean;
  powerPreference?: GPUPowerPreference;
  pixelRatio?: number;
  /**
   * Pre-created WebGL2 context to render into (three uses it as-is). Lets a
   * caller opt into context attributes three hardcodes away — e.g.
   * `preserveDrawingBuffer: true` so headless screenshot harnesses capture
   * real pixels (the compositor grabs AFTER the buffer swap). Pair with
   * `forceWebGL` so the WebGPU backend never races the provided GL context.
   */
  context?: WebGL2RenderingContext;
  /** Force the WebGL2 backend even where WebGPU is available. */
  forceWebGL?: boolean;
  /**
   * Request GPU timestamp queries so per-pass GPU time can be measured.
   *
   * MUST be decided before construction: three.js r182 copies this into the
   * renderer in its constructor (`this.trackTimestamp = parameters.trackTimestamp === true`)
   * and the backend snapshots it at ITS init. Flipping the flag afterwards is
   * silently ignored and every later resolveTimestampsAsync() warns.
   *
   * Off by default — timestamp queries are not free.
   */
  trackTimestamp?: boolean;
}

export class EngineRenderer {
  readonly native: THREE.WebGPURenderer;
  readonly cameras = new CameraStack();
  private _isWebGPU = false;
  /** When true, renderFrame() skips the draw+submit entirely — used to hold
   *  ALL GPU submits during a world transition so the old area's teardown
   *  disposals can never collide with an in-flight command buffer
   *  ("used in submit while destroyed"). The DOM loading overlay stays up. */
  paused = false;
  private _caps: RuntimeGpuCaps | null = null;
  postProcessing: PostProcessingPipeline | null = null;
  private _gpuTiming: GpuProfilerAggregator | null = null;
  private _gpuTimerBackend: IGpuTimerBackend | null = null;

  /** GPU per-pass timing source, or null when timestamps were not requested. */
  get gpuTiming(): GpuTimingSource | null {
    return this._gpuTiming;
  }

  private constructor(renderer: THREE.WebGPURenderer) {
    this.native = renderer;
  }

  static async create(config: RendererConfig): Promise<EngineRenderer> {
    // Pre-probe the adapter so we can ask for every feature the engine uses
    // (subgroups, shader-f16, timestamp-query, …) right at device creation.
    // Three.js WebGPUBackend accepts a `requiredFeatures` option that it
    // forwards into `adapter.requestDevice`.
    let requiredFeatures: GPUFeatureName[] = [];
    // Texture-heavy materials (biome splatting: N albedos + N normal maps) blow
    // past WebGPU's DEFAULT 16-sampled-textures-per-stage device limit even when
    // the adapter supports far more (desktop GPUs report 48+). Lift the sampler/
    // texture limits to whatever THIS adapter actually provides — requesting the
    // adapter's own values can never fail the device request.
    let maxSampledTextures = 16;
    let maxSamplers = 16;
    try {
      if (typeof navigator !== 'undefined' && 'gpu' in navigator) {
        const preAdapter = await (navigator as any).gpu.requestAdapter({
          powerPreference: config.powerPreference ?? 'high-performance',
        });
        if (preAdapter) {
          requiredFeatures = desiredRequiredFeatures(preAdapter as GPUAdapter);
          const limits = (preAdapter as GPUAdapter).limits;
          maxSampledTextures = limits.maxSampledTexturesPerShaderStage ?? 16;
          maxSamplers = limits.maxSamplersPerShaderStage ?? 16;
        }
      }
    } catch {
      // Adapter probing can fail in fingerprint-hardened browsers; safe to continue.
    }

    const renderer = new THREE.WebGPURenderer({
      canvas: config.canvas,
      context: config.context,
      forceWebGL: config.forceWebGL ?? false,
      antialias: config.antialias ?? true,
      alpha: config.alpha ?? false,
      powerPreference: config.powerPreference ?? 'high-performance',
      requiredFeatures,
      requiredLimits: {
        maxColorAttachmentBytesPerSample: 128,
        maxSampledTexturesPerShaderStage: maxSampledTextures,
        maxSamplersPerShaderStage: maxSamplers,
      },
      trackTimestamp: config.trackTimestamp ?? false,
    } as any);

    await renderer.init();

    const pixelRatio = Math.min(config.pixelRatio ?? window.devicePixelRatio, MAX_PIXEL_RATIO);
    renderer.setPixelRatio(pixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = DEFAULT_EXPOSURE;
    renderer.autoClear = false;

    const engineRenderer = new EngineRenderer(renderer);

    // Detect WebGPU vs WebGL fallback
    engineRenderer._isWebGPU = renderer.backend?.constructor?.name === 'WebGPUBackend';

    // ── Name the pipeline that failed ──────────────────────────────────────
    // WebGPU reports a broken pipeline as `[Invalid RenderPipeline "X"] is
    // invalid due to a PREVIOUS error`, once per frame, forever — and the
    // previous error, the one carrying the actual WGSL diagnostic, is an
    // UNCAPTURED device error that nothing was listening for. So the console
    // fills with a symptom and never states the cause (2026-09-15).
    //
    // three labels pipelines `renderPipeline_${material.name || material.type}_${material.id}`,
    // so the symptom at least identifies the material; this prints the reason.
    // Deduplicated by message: a compile failure repeats every frame.
    try {
      const device = (renderer.backend as { device?: GPUDevice } | undefined)?.device;
      if (device && typeof device.addEventListener === 'function') {
        const seen = new Set<string>();
        device.addEventListener('uncapturederror', (ev: Event) => {
          const msg = String((ev as { error?: { message?: string } }).error?.message ?? ev);
          const key = msg.slice(0, 200);
          if (seen.has(key)) return;
          seen.add(key);
          Logger.error('Renderer', `WebGPU device error (first of its kind):\n${msg}`);
        });
        Logger.debug('Renderer', 'WebGPU uncaptured-error reporting armed');
      }
    } catch { /* WebGL2 fallback, or a backend without a device handle */ }

    // Detect GPU features (subgroups, f16, timestamp-query, etc.) and publish
    // them through the singleton so downstream systems can branch on them.
    engineRenderer._caps = await detectRuntimeCaps(renderer);
    setRuntimeGpuCaps(engineRenderer._caps);

    // Wire GPU per-pass timing. Both this stack and src/debug/gpuprofiler/
    // shipped unreferenced for several releases — this is the single place the
    // engine constructs a timer. Constructing a backend does NOT guarantee it
    // reports real numbers: on WebGPU (the engine's primary backend),
    // WebGPUGpuTimer.available is hardcoded false — WebGPU can only attach
    // timestamp writes via a pass descriptor's `timestampWrites` field at
    // pass-creation time, and this call site never has one to give it (it
    // brackets the opaque `renderer.render()` call in `_renderFrameInner`
    // below, not a raw encoder/pass). Rather than fake it, that backend always
    // degrades to NullGpuTimer and logs why (see
    // docs/perf/2026-07-25-engine-graphics-findings.md, finding 1b — an
    // IGpuTimerBackend interface change would be required to fix this for
    // real, which is out of scope here). WebGL2GpuTimer's matching defect
    // (wrong extension-name fallback + WebGL1-only query API) was fixed by
    // 3ac5867 — correct as coded, but unverified on real hardware from this
    // repo's dev/CI environment, which exposes no timer-query extension at all
    // (finding 1a). PerfHUD and the editor profiler show whatever the selected
    // backend actually reports; "GPU timing unavailable" remains the common
    // case on WebGPU, not a fixed default.
    if (config.trackTimestamp) {
      // GpuTimerRendererLike.native.backend is deliberately typed as the
      // minimal `{ device?: unknown }` shape to avoid importing three's
      // Backend class. Three's own `Renderer.backend` field is nominally
      // typed as the base `Backend` class (no public `device`), even though
      // the concrete instance is always WebGPUBackend/WebGLBackend at
      // runtime — so TS's weak-type check rejects the structural match here.
      // engineRenderer really does satisfy the runtime contract; the cast
      // is confined to this call site.
      const backend = createGpuTimer(engineRenderer as unknown as GpuTimerRendererLike);
      engineRenderer._gpuTimerBackend = backend;
      engineRenderer._gpuTiming = new GpuProfilerAggregator(backend);
      Logger.info('Renderer', `GPU timing ${backend.available ? 'enabled' : 'requested but unavailable'}`);
    }

    Logger.info('Renderer', `Initialized (${engineRenderer._isWebGPU ? 'WebGPU' : 'WebGL2'})`);

    return engineRenderer;
  }

  /** Runtime GPU capability matrix (feature flags + limits). */
  get caps(): RuntimeGpuCaps {
    // _caps is populated in create(); if access happens before that (tests
    // constructing a renderer manually), fall back to the singleton accessor.
    if (this._caps) return this._caps;
    // Lazy import via dynamic-safe access to avoid circular init.
    const lazy = (globalThis as any).__engineXRuntimeGpuCaps as RuntimeGpuCaps | undefined;
    if (lazy) return lazy;
    return {
      hasWebGPU: this._isWebGPU,
      backend: this._isWebGPU ? 'webgpu' : 'webgl2',
      features: new Set(),
      limits: {
        maxComputeWorkgroupSizeX: 256,
        maxComputeWorkgroupSizeY: 256,
        maxComputeWorkgroupSizeZ: 64,
        maxComputeInvocationsPerWorkgroup: 256,
        maxComputeWorkgroupsPerDimension: 65535,
        maxStorageBufferBindingSize: 128 * 1024 * 1024,
        maxStorageTexturesPerShaderStage: 4,
        maxUniformBufferBindingSize: 64 * 1024,
        maxBindGroups: 4,
        maxBindingsPerBindGroup: 640,
        maxTextureDimension2D: 8192,
        maxTextureDimension3D: 2048,
        maxTextureArrayLayers: 256,
        maxColorAttachments: 8,
        maxColorAttachmentBytesPerSample: 32,
      },
      adapterInfo: { vendor: '', architecture: '', device: '', description: '' },
      adapterType: 'unknown',
      hasF16: false,
      hasSubgroups: false,
      hasSubgroupsF16: false,
      hasTimestampQuery: false,
      hasIndirectFirstInstance: false,
      hasF32Filterable: false,
      hasR11G11B10RenderTarget: false,
      hasBC: false,
      hasASTC: false,
      hasETC2: false,
    };
  }

  get isWebGPU(): boolean {
    return this._isWebGPU;
  }

  private _lastSizeW = 0;
  private _lastSizeH = 0;
  /** Reusable scratch for renderer.getSize() in the per-camera render path. */
  private readonly _tmpSize = new THREE.Vector2();

  resize(width: number, height: number): void {
    // Guard against degenerate resize events (window minimize often delivers
    // 0-height). width/height = Infinity would silently propagate NaN through
    // every downstream projection-matrix consumer for the entire frame.
    // Must come BEFORE setSize — Three.js will collapse the framebuffer to 0×0
    // otherwise and stay there until the next valid resize.
    if (width <= 0 || height <= 0) {
      return;
    }

    this.native.setSize(width, height);

    // Update camera aspect ratios
    for (const entry of this.cameras.getEntries()) {
      const cam = entry.camera;
      if (cam instanceof THREE.PerspectiveCamera) {
        cam.aspect = width / height;
        cam.updateProjectionMatrix();
      } else if (cam instanceof THREE.OrthographicCamera) {
        // For orthographic cameras we keep the world-units-per-pixel ratio
        // constant across resizes. We need to scale both the width and the
        // height of the frustum — the previous implementation only remapped
        // left/right, freezing top/bottom at the original viewport height.
        const prevW = this._lastSizeW || width;
        const prevH = this._lastSizeH || height;
        const prevHalfH = cam.top;
        const prevHalfW = cam.right;
        // Guard against degenerate camera setup (e.g. top=0 ortho camera).
        // Without these guards the half-width or half-height collapses to
        // zero, freezing the frustum and making nothing render.
        const unitsPerPxX = prevW > 0 && prevHalfW > 0 ? (prevHalfW * 2) / prevW : 1;
        const unitsPerPxY = prevH > 0 && prevHalfH > 0 ? (prevHalfH * 2) / prevH : 1;
        const halfW = (width * unitsPerPxX) / 2;
        const halfH = (height * unitsPerPxY) / 2;
        cam.left = -halfW;
        cam.right = halfW;
        cam.top = halfH;
        cam.bottom = -halfH;
        cam.updateProjectionMatrix();
      }
    }
    this._lastSizeW = width;
    this._lastSizeH = height;
  }

  /**
   * Create a post-processing pipeline for a scene and camera.
   */
  createPostProcessing(
    scene: THREE.Scene,
    camera: THREE.Camera,
    config?: PostProcessingConfig,
  ): PostProcessingPipeline {
    this.postProcessing = new PostProcessingPipeline(
      this.native, scene, camera, config,
    );
    return this.postProcessing;
  }

  /**
   * Render one frame, timing it as a single GPU pass named 'frame' when
   * timestamps are enabled.
   *
   * The body lives in _renderFrameInner because it has THREE exit paths (XR
   * early return, post-processing early return, and the camera loop). An
   * inline begin/end pair would leak an unclosed scope on two of them, and on
   * any throw — try/finally is what makes the scope balanced on every path.
   */
  renderFrame(scene: THREE.Scene): void {
    if (this.paused) return; // world-transition hold — no submits while the old world tears down
    const backend = this._gpuTimerBackend;
    if (!backend) {
      this._renderFrameInner(scene);
      return;
    }
    let opened = false;
    try {
      // Inside the try: a timer fault must never break the render loop.
      // Instrumentation is not allowed to take down the frame. `opened`
      // gates the matching endPass below — a scope that never opened must
      // never be closed.
      try {
        backend.beginPass('frame');
        opened = true;
      } catch (err) {
        Logger.warn('Renderer', `GPU timer beginPass failed — timing disabled this frame: ${err}`);
      }
      this._renderFrameInner(scene);
    } finally {
      if (opened) {
        try {
          backend.endPass('frame');
          // endFrame() only SCHEDULES async readback; tick() then pulls whichever
          // frame has actually resolved (typically 1-2 frames behind). Neither
          // stalls the pipeline.
          backend.endFrame();
          this._gpuTiming?.tick();
        } catch (err) {
          Logger.warn('Renderer', `GPU timer readback failed — timing disabled this frame: ${err}`);
        }
      }
    }
  }

  /**
   * Render all cameras in the stack against the given scene.
   */
  private _renderFrameInner(scene: THREE.Scene): void {
    const entries = this.cameras.getEntries();
    const renderer = this.native;

    // XR presentation: WebXRManager owns pose/stereo/framebuffer — it still needs
    // ONE render() call per frame, but the mono fullscreen post-processing quad and
    // overlay cameras (viewport/scissor) would corrupt the XR layer, so both are
    // bypassed. Falls back to entries[0] even when XRCameraOverrideSystem has
    // disabled the whole stack (its suppression targets the flat paths, not this one).
    if (renderer.xr?.isPresenting) {
      const primary = entries.find((e) => e.enabled !== false) ?? entries[0];
      if (primary) {
        renderer.setScissorTest(false);
        renderer.render(scene, primary.camera);
      }
      return;
    }

    // If post-processing is enabled and primary camera is active, use it
    if (this.postProcessing?.enabled && entries.length > 0 && entries[0].enabled !== false) {
      const primaryCamera = entries[0].camera;
      // Reset any scissor left active by a previous frame's overlay camera:
      // postProcessing.render() bypasses _renderCamera, so without this it
      // would clip the primary render to the stale scissor rect.
      renderer.setScissorTest(false);
      this.postProcessing.setCamera(primaryCamera);
      this.postProcessing.render();

      // Render remaining cameras (overlays, etc.) without post-processing
      for (let i = 1; i < entries.length; i++) {
        if (entries[i].enabled === false) continue;
        this._renderCamera(renderer, scene, entries[i]);
      }
      return;
    }

    for (const entry of entries) {
      if (entry.enabled === false) continue;
      this._renderCamera(renderer, scene, entry);
    }
  }

  private _renderCamera(
    renderer: THREE.WebGPURenderer,
    scene: THREE.Scene,
    entry: CameraEntry,
  ): void {
    // Viewport
    if (entry.viewport) {
      const vp = entry.viewport;
      // Use LOGICAL size (getSize), NOT domElement.width/height (physical =
      // logical × pixelRatio). Three.js r182's WebGPU setViewport/setScissor
      // take logical pixels and multiply the stored rect by _pixelRatio at
      // render time (Renderer _getFrameBufferTarget). Feeding physical pixels
      // here double-applies the DPR, pushing the rect off-screen whenever
      // pixelRatio≠1 (every HiDPI/Retina display).
      const sz = renderer.getSize(this._tmpSize);
      const w = sz.x;
      const h = sz.y;
      renderer.setViewport(vp.x * w, vp.y * h, vp.width * w, vp.height * h);
      renderer.setScissor(vp.x * w, vp.y * h, vp.width * w, vp.height * h);
      renderer.setScissorTest(true);
    } else {
      renderer.setScissorTest(false);
    }

    // Clear
    renderer.autoClear = false;
    if (entry.clearColor || entry.clearDepth || entry.clearStencil) {
      renderer.clear(
        entry.clearColor ?? false,
        entry.clearDepth ?? false,
        entry.clearStencil ?? false,
      );
    }

    // Layer mask
    if (entry.layers !== undefined) {
      entry.camera.layers.mask = entry.layers;
    }

    renderer.render(scene, entry.camera);
  }

  async requestScreenshot(mimeType = 'image/png', quality = 0.92): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.native.domElement.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to capture screenshot'));
        },
        mimeType,
        quality,
      );
    });
  }

  dispose(): void {
    this.postProcessing?.dispose();
    this.postProcessing = null;
    // _gpuTimerBackend owns real GPU resources (WebGL2 query objects, or a
    // WebGPU query set + four buffers) — leaving it undisposed leaks them.
    // Guarded: a throwing backend must not break the rest of teardown.
    try { this._gpuTimerBackend?.dispose(); } catch { /* already gone */ }
    this._gpuTimerBackend = null;
    this._gpuTiming = null;
    this.native.dispose();
  }
}
